import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Gauge, Plus, Trash2, Pencil, Loader2, Zap,
  AlertTriangle, CheckCircle, TrendingUp, TrendingDown,
  DollarSign, Tv, Droplets, Utensils, Car, Shield, Wine,
  Wrench, Play, MoreHorizontal, Lightbulb, RefreshCw,
  ChevronRight, ArrowUpRight
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from "recharts";

const EXPENSE_CATEGORIES = [
  { value: "cable", label: "Cable/Internet", icon: Tv, color: "#10B981" },
  { value: "electric", label: "Electric", icon: Zap, color: "#F59E0B" },
  { value: "water", label: "Water", icon: Droplets, color: "#3B82F6" },
  { value: "garbage", label: "Garbage", icon: Trash2, color: "#8B5CF6" },
  { value: "food", label: "Food/Groceries", icon: Utensils, color: "#EC4899" },
  { value: "car_payment", label: "Car Payment", icon: Car, color: "#EF4444" },
  { value: "insurance", label: "Insurance", icon: Shield, color: "#14B8A6" },
  { value: "night_out", label: "Night Out", icon: Wine, color: "#F97316" },
  { value: "maintenance", label: "Maintenance", icon: Wrench, color: "#6366F1" },
  { value: "subscription", label: "Subscriptions", icon: Play, color: "#E11D48" },
  { value: "loan_payments", label: "Loan Payments", icon: DollarSign, color: "#78716C" },
  { value: "other", label: "Other", icon: MoreHorizontal, color: "#6B7280" },
];

const getCatMeta = (val) => EXPENSE_CATEGORIES.find((c) => c.value === val) || EXPENSE_CATEGORIES[11];

const fmtCurrency = (v) =>
  parseFloat(v || 0).toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const formatMonth = (m) => {
  const d = new Date(m + "-01");
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const StatusBadge = ({ status, pct }) => {
  const config = {
    over: { bg: "bg-red-500/10", text: "text-red-400", label: "Over Budget" },
    warning: { bg: "bg-amber-500/10", text: "text-amber-400", label: "Warning" },
    under: { bg: "bg-emerald-500/10", text: "text-emerald-400", label: "On Track" },
    unbudgeted: { bg: "bg-zinc-500/10", text: "text-zinc-400", label: "No Budget" },
  };
  const c = config[status] || config.unbudgeted;
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${c.bg} ${c.text}`} data-testid={`status-${status}`}>
      {c.label} {status !== "unbudgeted" && `(${pct}%)`}
    </span>
  );
};

const ProgressBar = ({ pct, status }) => {
  const color = status === "over" ? "bg-red-500" : status === "warning" ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} transition-all duration-500 rounded-full`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
};

export default function BudgetPlanner() {
  const { api } = useAuth();
  const [month, setMonth] = useState(getCurrentMonth());
  const [comparison, setComparison] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddBudget, setShowAddBudget] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [formData, setFormData] = useState({ category: "", limit: "" });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, histRes] = await Promise.all([
        api.get(`/budgets/vs-actual?month=${month}`),
        api.get("/budgets/history"),
      ]);
      setComparison(compRes.data);
      setHistory(histRes.data);
    } catch {
      toast.error("Failed to load budget data");
    } finally {
      setLoading(false);
    }
  }, [api, month]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveBudget = async () => {
    if (!formData.category || !formData.limit || parseFloat(formData.limit) <= 0) {
      toast.error("Category and a positive limit are required");
      return;
    }
    try {
      if (editingBudget) {
        await api.put(`/budgets/${editingBudget.budget_id}`, {
          limit: parseFloat(formData.limit),
        });
        toast.success("Budget updated");
      } else {
        await api.post("/budgets", {
          category: formData.category,
          limit: parseFloat(formData.limit),
          month,
        });
        toast.success("Budget created");
      }
      setShowAddBudget(false);
      setEditingBudget(null);
      setFormData({ category: "", limit: "" });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save budget");
    }
  };

  const deleteBudget = async (budgetId) => {
    try {
      await api.delete(`/budgets/${budgetId}`);
      toast.success("Budget removed");
      loadData();
    } catch {
      toast.error("Failed to delete budget");
    }
  };

  const openEdit = (cat) => {
    setEditingBudget(cat);
    setFormData({ category: cat.category, limit: String(cat.limit) });
    setShowAddBudget(true);
  };

  const runAiOptimize = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/budgets/ai-optimize", { month });
      setAiData(res.data);
      toast.success("AI analysis complete");
    } catch {
      toast.error("Failed to generate AI suggestions");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestion = async (suggestion) => {
    try {
      await api.post("/budgets", {
        category: suggestion.category,
        limit: suggestion.suggested_limit,
        month,
      });
      toast.success(`Budget for ${suggestion.category} updated to ${fmtCurrency(suggestion.suggested_limit)}`);
      loadData();
    } catch {
      toast.error("Failed to apply suggestion");
    }
  };

  if (loading) {
    return (
      <Layout title="Budget Planner">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  const summary = comparison?.summary || {};
  const categories = comparison?.categories || [];
  const budgeted = categories.filter((c) => c.status !== "unbudgeted");
  const unbudgeted = categories.filter((c) => c.status === "unbudgeted");

  const chartData = budgeted.map((c) => ({
    name: getCatMeta(c.category).label,
    Budget: c.limit,
    Spent: c.spent,
    category: c.category,
  }));

  const gradeColors = {
    A: "text-emerald-400 bg-emerald-500/10",
    B: "text-cyan-400 bg-cyan-500/10",
    C: "text-amber-400 bg-amber-500/10",
    D: "text-orange-400 bg-orange-500/10",
    F: "text-red-400 bg-red-500/10",
  };

  return (
    <Layout title="Budget Planner">
      <div className="space-y-6" data-testid="budget-planner-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Budget Planner</h1>
            <p className="text-sm text-zinc-500 mt-1">Set limits, track spending, stay on target</p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-black/50 border-white/10 rounded-sm w-44 text-sm text-zinc-300"
              data-testid="month-picker"
            />
            <Button
              onClick={() => { setEditingBudget(null); setFormData({ category: "", limit: "" }); setShowAddBudget(true); }}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              data-testid="add-budget-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Budget
            </Button>
            <Button
              onClick={runAiOptimize}
              disabled={aiLoading}
              variant="outline"
              size="sm"
              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-sm"
              data-testid="ai-optimize-btn"
            >
              {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5 mr-1" />}
              AI Optimize
            </Button>
            <Button onClick={loadData} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-sm" data-testid="refresh-budget-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Month Title */}
        <p className="text-lg font-semibold text-zinc-300">{formatMonth(month)}</p>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-total-budget">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Budget</p>
              <p className="text-2xl font-bold font-mono text-white mt-1">{fmtCurrency(summary.total_budget)}</p>
              <p className="text-xs text-zinc-600 mt-1">{summary.budgeted_categories || 0} categories</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-total-spent">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Spent</p>
              <p className={`text-2xl font-bold font-mono mt-1 ${summary.overall_pct > 100 ? "text-red-400" : "text-white"}`}>{fmtCurrency(summary.total_spent)}</p>
              <p className="text-xs text-zinc-600 mt-1">{summary.overall_pct || 0}% of budget</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-remaining">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Remaining</p>
              <p className={`text-2xl font-bold font-mono mt-1 ${summary.total_remaining >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtCurrency(summary.total_remaining)}</p>
              <p className="text-xs text-zinc-600 mt-1">{summary.total_remaining >= 0 ? "Under budget" : "Over budget"}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stat-alerts">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Alerts</p>
              <div className="flex items-center gap-3 mt-1">
                {summary.over_budget_count > 0 && (
                  <span className="text-red-400 text-sm font-mono flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> {summary.over_budget_count} over
                  </span>
                )}
                {summary.warning_count > 0 && (
                  <span className="text-amber-400 text-sm font-mono flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> {summary.warning_count} warning
                  </span>
                )}
                {(summary.over_budget_count === 0 && summary.warning_count === 0) && (
                  <span className="text-emerald-400 text-sm font-mono flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> All clear
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-600 mt-1">{summary.under_budget_count || 0} on track</p>
            </CardContent>
          </Card>
        </div>

        {/* Overall Progress */}
        {summary.total_budget > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-400">Overall Budget Utilization</span>
                <span className={`text-sm font-mono ${summary.overall_pct > 100 ? "text-red-400" : summary.overall_pct > 75 ? "text-amber-400" : "text-emerald-400"}`}>
                  {summary.overall_pct}%
                </span>
              </div>
              <ProgressBar
                pct={summary.overall_pct}
                status={summary.overall_pct > 100 ? "over" : summary.overall_pct > 75 ? "warning" : "under"}
              />
            </CardContent>
          </Card>
        )}

        {budgeted.length === 0 && unbudgeted.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="py-16 text-center">
              <Gauge className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">No budgets set for {formatMonth(month)}</p>
              <p className="text-zinc-600 text-xs mt-1">Click "Add Budget" to set spending limits</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Budget vs Actual Chart */}
            {chartData.length > 0 && (
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-emerald-400" /> Budget vs. Actual Spending
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[260px]" data-testid="budget-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#71717a" }} interval={0} angle={-20} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "K" : v}`} width={50} />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div className="bg-black/95 border border-white/10 rounded-sm p-3 text-xs">
                                <p className="text-zinc-400 mb-1.5">{label}</p>
                                {payload.map((p) => (
                                  <div key={p.dataKey} className="flex items-center gap-2 mb-0.5">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                                    <span className="text-zinc-400">{p.dataKey}:</span>
                                    <span className="text-white font-mono">{fmtCurrency(p.value)}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />
                        <Bar dataKey="Budget" fill="#3b82f6" opacity={0.4} radius={[2, 2, 0, 0]} />
                        <Bar dataKey="Spent" radius={[2, 2, 0, 0]}>
                          {chartData.map((entry, i) => (
                            <Cell
                              key={i}
                              fill={entry.Spent > entry.Budget ? "#ef4444" : entry.Spent > entry.Budget * 0.75 ? "#f59e0b" : "#10b981"}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Category Cards */}
            <div className="space-y-3">
              <h2 className="text-sm text-zinc-500 uppercase tracking-wider">Budgeted Categories</h2>
              {budgeted.map((cat) => {
                const meta = getCatMeta(cat.category);
                const Icon = meta.icon;
                return (
                  <Card key={cat.budget_id} className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid={`budget-card-${cat.category}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${meta.color}15` }}>
                          <Icon className="w-4.5 h-4.5" style={{ color: meta.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-white">{meta.label}</span>
                            <div className="flex items-center gap-2">
                              <StatusBadge status={cat.status} pct={cat.pct} />
                              <Button
                                onClick={() => openEdit(cat)}
                                variant="ghost" size="sm"
                                className="h-6 w-6 p-0 text-zinc-600 hover:text-white"
                                data-testid={`edit-budget-${cat.category}`}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => deleteBudget(cat.budget_id)}
                                variant="ghost" size="sm"
                                className="h-6 w-6 p-0 text-zinc-600 hover:text-red-400"
                                data-testid={`delete-budget-${cat.category}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          <ProgressBar pct={cat.pct} status={cat.status} />
                          <div className="flex items-center justify-between mt-1.5 text-xs">
                            <span className="text-zinc-500">{fmtCurrency(cat.spent)} of {fmtCurrency(cat.limit)}</span>
                            <span className={`font-mono ${cat.remaining >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              {cat.remaining >= 0 ? `${fmtCurrency(cat.remaining)} left` : `${fmtCurrency(Math.abs(cat.remaining))} over`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Unbudgeted Spending */}
            {unbudgeted.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm text-zinc-500 uppercase tracking-wider flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Unbudgeted Spending
                </h2>
                {unbudgeted.map((cat) => {
                  const meta = getCatMeta(cat.category);
                  const Icon = meta.icon;
                  return (
                    <Card key={cat.category} className="bg-[#0A0A0A] border-amber-500/10 rounded-none" data-testid={`unbudgeted-${cat.category}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${meta.color}15` }}>
                            <Icon className="w-4.5 h-4.5" style={{ color: meta.color }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-white">{meta.label}</span>
                              <span className="text-sm font-mono text-amber-400">{fmtCurrency(cat.spent)}</span>
                            </div>
                            <p className="text-xs text-zinc-600 mt-0.5">{cat.transaction_count} transactions - no budget set</p>
                          </div>
                          <Button
                            onClick={() => { setEditingBudget(null); setFormData({ category: cat.category, limit: String(Math.ceil(cat.spent / 50) * 50) }); setShowAddBudget(true); }}
                            variant="outline" size="sm"
                            className="border-amber-500/20 text-amber-400 hover:bg-amber-500/10 rounded-sm text-xs"
                            data-testid={`set-budget-${cat.category}`}
                          >
                            Set Budget
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Budget History */}
        {history.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-400" /> Budget Adherence History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto" data-testid="budget-history-table">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-zinc-500 py-2 px-3 font-medium">Month</th>
                      <th className="text-right text-zinc-500 py-2 px-3 font-medium">Budget</th>
                      <th className="text-right text-zinc-500 py-2 px-3 font-medium">Spent</th>
                      <th className="text-right text-zinc-500 py-2 px-3 font-medium">Utilization</th>
                      <th className="text-right text-zinc-500 py-2 px-3 font-medium">Over Budget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice().reverse().map((h) => (
                      <tr key={h.month} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-2.5 px-3 text-zinc-300 font-mono">{formatMonth(h.month)}</td>
                        <td className="py-2.5 px-3 text-right text-zinc-300 font-mono">{fmtCurrency(h.total_budget)}</td>
                        <td className="py-2.5 px-3 text-right text-zinc-300 font-mono">{fmtCurrency(h.total_spent)}</td>
                        <td className={`py-2.5 px-3 text-right font-mono ${h.pct > 100 ? "text-red-400" : h.pct > 75 ? "text-amber-400" : "text-emerald-400"}`}>
                          {h.pct}%
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          {h.over_budget_count > 0 ? (
                            <span className="text-red-400 font-mono">{h.over_budget_count} categories</span>
                          ) : (
                            <span className="text-emerald-400 font-mono">None</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Optimization */}
        {aiData && (
          <Card className="bg-[#0A0A0A] border-cyan-500/10 rounded-none" data-testid="ai-budget-optimization">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-cyan-400" /> AI Budget Optimization
                {aiData.overall_grade && (
                  <span className={`ml-auto text-sm font-bold px-2.5 py-0.5 rounded-sm ${gradeColors[aiData.overall_grade] || gradeColors.C}`}>
                    Grade: {aiData.overall_grade}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {aiData.summary && (
                <p className="text-sm text-zinc-300 leading-relaxed">{aiData.summary}</p>
              )}

              {aiData.recommended_savings_target > 0 && (
                <div className="bg-emerald-500/5 border border-emerald-500/10 p-3 rounded-sm">
                  <p className="text-xs text-emerald-400 uppercase tracking-wider">Recommended Monthly Savings</p>
                  <p className="text-xl font-bold font-mono text-emerald-300 mt-1">{fmtCurrency(aiData.recommended_savings_target)}</p>
                </div>
              )}

              {aiData.suggested_budgets?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Suggested Budget Adjustments</p>
                  <div className="space-y-2">
                    {aiData.suggested_budgets.map((s, i) => (
                      <div key={i} className="bg-white/[0.02] border border-white/5 p-3 rounded-sm flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-white capitalize font-medium">{s.category}</span>
                            <span className="text-xs text-zinc-500">
                              {fmtCurrency(s.current_limit)} <ChevronRight className="w-3 h-3 inline" /> {fmtCurrency(s.suggested_limit)}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-400">{s.reason}</p>
                        </div>
                        <Button
                          onClick={() => applyAiSuggestion(s)}
                          variant="outline" size="sm"
                          className="border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10 rounded-sm text-xs flex-shrink-0"
                          data-testid={`apply-suggestion-${i}`}
                        >
                          Apply
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiData.savings_tips?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Savings Tips</p>
                  <div className="space-y-1.5">
                    {aiData.savings_tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Zap className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                        {tip}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiData.warnings?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Warnings</p>
                  <div className="space-y-1.5">
                    {aiData.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-red-300">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                        {w}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Budget Dialog */}
        <Dialog open={showAddBudget} onOpenChange={(open) => { setShowAddBudget(open); if (!open) { setEditingBudget(null); setFormData({ category: "", limit: "" }); } }}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-md" data-testid="budget-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">{editingBudget ? "Edit Budget" : "Add Budget"}</DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">Set a monthly spending limit for a category</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                  disabled={!!editingBudget}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="budget-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Monthly Limit ($)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.limit}
                  onChange={(e) => setFormData({ ...formData, limit: e.target.value })}
                  placeholder="500"
                  className="bg-black/50 border-white/10 rounded-sm"
                  data-testid="budget-limit-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={saveBudget} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="save-budget-btn">
                  {editingBudget ? "Update" : "Create"} Budget
                </Button>
                <Button onClick={() => { setShowAddBudget(false); setEditingBudget(null); }} variant="outline" className="border-white/10 text-zinc-400 rounded-sm" data-testid="cancel-budget-btn">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
